import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronRight,
  ChevronDown,
  PanelLeftClose,
  PanelLeft,
  Grid3X3,
  ChevronUp,
} from 'lucide-react';
import { useSidebar } from '../context/SidebarContext';
import { moduleColorSchemes, getAllModules } from '../config/moduleConfig';
import { useAuth } from '../context/AuthContext';
import brandingConfig from '../config/brandingConfig';
/**
 * ModuleSidebar Component
 *
 * Renders module navigation with permission-based filtering.
 *
 * FILTERING RULES:
 *   1. item.permission is set   → visible only if hasPermission(item.permission) === true
 *   2. item.permission not set  → always visible (unguarded item)
 *   3. Parent with children     → visible only if at least one child passes rule 1/2
 *                                 Hidden when ALL children are hidden
 */
export default function ModuleSidebar({ module }) {
  const { isCollapsed, setIsCollapsed, hasProvider } = useSidebar();
  const [localSidebarOpen, setLocalSidebarOpen] = useState(true);
  const sidebarOpen = hasProvider ? !isCollapsed : localSidebarOpen;

  const setSidebarOpen = (open) => {
    if (hasProvider) setIsCollapsed(!open);
    setLocalSidebarOpen(open);
  };

  const [openMenus, setOpenMenus] = useState({});
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hideTimeout, setHideTimeout] = useState(null);
  const [showModuleSwitcher, setShowModuleSwitcher] = useState(false);
  const moduleSwitcherRef = useRef(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { hasRole, hasModuleAccess, hasPermission } = useAuth();

  // ─── Module switcher list (subscription + permission gated) ────────────────
  const modulePermissionMap = {
    BACKOFFICE: 'back_office', INVENTORY: 'inventory', REPORTS: 'reports',
    CRM: 'crm', HRM: 'hrm', QR_MENU: 'qr_menu', POS: 'pos',
    KDS: 'kds', CAPTAIN_APP: 'captain_app', INTEGRATIONS: 'integrations',
    AI_INSIGHTS: 'ai_insights',
  };
  const switchableModules = getAllModules().filter((m) => {
    if (m.isComingSoon) return false;
    if (!hasModuleAccess(m.id)) return false;
    const perm = modulePermissionMap[m.id];
    if (!perm) return false;
    return hasPermission(perm);
  });

  // Close module switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (moduleSwitcherRef.current && !moduleSwitcherRef.current.contains(event.target)) {
        setShowModuleSwitcher(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ─── Color scheme ──────────────────────────────────────────────────────────
  const colors = module
    ? moduleColorSchemes[module.color] || moduleColorSchemes.teal
    : moduleColorSchemes.teal;

  // ─── Permission check helper ───────────────────────────────────────────────
  // Returns true if this single item (leaf or child) should be visible.
  // Rule: if item has a `permission` field → check it; otherwise always show.
  const canSeeItem = (item) => {
    if (!item.permission) return true;
    return hasPermission(item.permission);
  };

  // ─── Filtered menu items ───────────────────────────────────────────────────
  // Built fresh whenever the module or the user's permissions change.
  const filteredMenuItems = useMemo(() => {
    if (!module?.menuItems) return [];

    return module.menuItems.reduce((acc, item) => {
      const hasChildren = Array.isArray(item.children) && item.children.length > 0;

      if (hasChildren) {
        // Filter children individually, then keep parent only if ≥1 child survives
        const visibleChildren = item.children.filter(canSeeItem);
        if (visibleChildren.length > 0) {
          acc.push({ ...item, children: visibleChildren });
        }
        // If ALL children are hidden → parent is also hidden (do nothing)
      } else {
        // Leaf item — show/hide based on its own permission
        if (canSeeItem(item)) acc.push(item);
      }

      return acc;
    }, []);
  }, [module, hasPermission]); // re-run if module or permission state changes

  // ─── Auto-open menus whose child is currently active ──────────────────────
  useEffect(() => {
    if (!filteredMenuItems.length) return;

    const newOpenMenus = {};
    filteredMenuItems.forEach((item) => {
      if (item.children?.length) {
        const hasActiveChild = item.children.some((child) => location.pathname === child.path);
        if (hasActiveChild) newOpenMenus[item.id] = true;
      }
    });

    if (Object.keys(newOpenMenus).length > 0) {
      setOpenMenus((prev) => ({ ...prev, ...newOpenMenus }));
    }
  }, [location.pathname, filteredMenuItems]);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const handleToggle = () => setSidebarOpen(!sidebarOpen);

  const handleMenuClick = (path, parentId = null) => {
    if (parentId) setOpenMenus((prev) => ({ ...prev, [parentId]: true }));
    navigate(path);
  };

  const handleModuleSwitch = (targetModule) => {
    setShowModuleSwitcher(false);
    const path =
      targetModule.id === 'BACKOFFICE' ? '/admin/dashboard' : targetModule.basePath;
    navigate(path);
  };

  const isActive = (path) => location.pathname === path;

  const handlePopoverShow = (menuId) => {
    if (hideTimeout) { clearTimeout(hideTimeout); setHideTimeout(null); }
    setHoveredMenu(menuId);
  };

  const handlePopoverHide = () => {
    const timeout = setTimeout(() => setHoveredMenu(null), 200);
    setHideTimeout(timeout);
  };

  const handlePopoverEnter = () => {
    if (hideTimeout) { clearTimeout(hideTimeout); setHideTimeout(null); }
  };

  if (!module) return null;

  const ModuleIcon = module.icon;

  return (
    <aside
      className={`${
        sidebarOpen ? 'w-60' : 'w-20'
      } bg-white border-r border-slate-200 transition-all duration-300 flex flex-col shadow-sm flex-shrink-0`}
    >
      {/* ── Module Header with Switcher ─────────────────────────────────────── */}
      <div className="border-b border-slate-200 p-4 relative" ref={moduleSwitcherRef}>
        <button
          onClick={() => setShowModuleSwitcher(!showModuleSwitcher)}
          className={`w-full flex items-center gap-3 rounded-lg p-2 transition-all group ${
            showModuleSwitcher ? 'bg-slate-100' : 'hover:bg-slate-50'
          }`}
          title="Click to switch module"
        >
          <div className="relative">
            <div
              className={`w-10 h-10 bg-gradient-to-br ${module.gradient} rounded-lg flex items-center justify-center shadow-sm transition-transform group-hover:scale-105`}
            >
              <ModuleIcon className="w-5 h-5 text-white" />
            </div>
            <div
              className={`absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center shadow-sm transition-all ${
                showModuleSwitcher
                  ? 'bg-teal-500 text-white'
                  : 'bg-white border border-slate-200 text-slate-400 group-hover:border-teal-300 group-hover:text-teal-500'
              }`}
            >
              {showModuleSwitcher ? (
                <ChevronUp className="w-3 h-3" />
              ) : (
                <Grid3X3 className="w-3 h-3" />
              )}
            </div>
          </div>

          {sidebarOpen && (
            <div className="flex-1 min-w-0 text-left">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-bold text-slate-900 leading-tight truncate">
                  {module.name}
                </h2>
                <ChevronDown
                  className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                    showModuleSwitcher ? 'rotate-180' : ''
                  }`}
                />
              </div>
              <p className="text-[11px] text-slate-500 truncate">Click to switch module</p>
            </div>
          )}
        </button>

        {/* Module Switcher Dropdown */}
        {showModuleSwitcher && (
          <div
            className={`absolute ${
              sidebarOpen ? 'left-3 right-3' : 'left-full ml-2'
            } top-full mt-1 bg-white rounded-xl shadow-2xl border border-slate-200 py-2 z-50 min-w-[240px]`}
          >
            <div className="px-4 py-3 border-b border-slate-100">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-500 uppercase tracking-wide">
                <Grid3X3 className="w-3.5 h-3.5" />
                <span>Switch Module</span>
              </div>
            </div>
            <div className="py-2 max-h-[400px] overflow-y-auto">
              <style>{`
                .module-switcher-scroll::-webkit-scrollbar { width: 6px; }
                .module-switcher-scroll::-webkit-scrollbar-track { background: transparent; margin: 4px 0; }
                .module-switcher-scroll::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
                .module-switcher-scroll::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
              `}</style>
              <div className="module-switcher-scroll">
                {switchableModules.map((m) => {
                  const MIcon = m.icon;
                  const isCurrentModule = m.id === module.id;
                  return (
                    <button
                      key={m.id}
                      onClick={() => handleModuleSwitch(m)}
                      disabled={isCurrentModule}
                      className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${
                        isCurrentModule
                          ? 'bg-teal-50 border-l-2 border-teal-500'
                          : 'hover:bg-slate-50 border-l-2 border-transparent hover:border-slate-300'
                      }`}
                    >
                      <div
                        className={`w-9 h-9 bg-gradient-to-br ${m.gradient} rounded-lg flex items-center justify-center flex-shrink-0 shadow-sm`}
                      >
                        <MIcon className="w-4.5 h-4.5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-semibold truncate ${
                            isCurrentModule ? 'text-teal-700' : 'text-slate-700'
                          }`}
                        >
                          {m.name}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{m.description}</p>
                      </div>
                      {isCurrentModule && (
                        <span className="text-[10px] font-semibold text-teal-600 bg-teal-100 px-2 py-0.5 rounded-full">
                          Active
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="border-t border-slate-100 pt-2 mt-1 px-3">
              <button
                onClick={() => { setShowModuleSwitcher(false); navigate('/select-role'); }}
                className="w-full flex items-center justify-center gap-2 px-3 py-2.5 text-xs font-semibold text-teal-600 hover:text-teal-700 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors"
              >
                <Grid3X3 className="w-3.5 h-3.5" />
                View All Modules
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Toggle Button ───────────────────────────────────────────────────── */}
      <div className="px-3 py-2 border-b border-slate-200">
        <button
          onClick={handleToggle}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
          title={sidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
        >
          {sidebarOpen ? (
            <>
              <PanelLeftClose className="w-5 h-5" />
              <span className="text-sm font-medium">Collapse</span>
            </>
          ) : (
            <PanelLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* ── Menu Items ──────────────────────────────────────────────────────── */}
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
        {filteredMenuItems.length === 0 ? (
          /* Empty state — all items filtered out due to missing permissions */
          sidebarOpen && (
            <div className="flex flex-col items-center justify-center py-8 px-3 text-center">
              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <ModuleIcon className="w-5 h-5 text-slate-400" />
              </div>
              <p className="text-xs font-medium text-slate-500">No accessible pages</p>
              <p className="text-[11px] text-slate-400 mt-1">
                Contact your admin to request access
              </p>
            </div>
          )
        ) : (
          filteredMenuItems.map((item) => {
            const Icon = item.icon;
            const hasChildren = Array.isArray(item.children) && item.children.length > 0;
            const hasActiveChild =
              hasChildren &&
              item.children.some((child) => location.pathname === child.path);
            const active = isActive(item.path) || hasActiveChild;
            const isOpen = openMenus[item.id] ?? false;
            const showPopover = !sidebarOpen && hasChildren && hoveredMenu === item.id;

            return (
              <div
                key={item.id}
                className="relative"
                onMouseEnter={() => !sidebarOpen && hasChildren && handlePopoverShow(item.id)}
                onMouseLeave={() => !sidebarOpen && hasChildren && handlePopoverHide()}
              >
                {/* Main Menu Button */}
                <button
                  onClick={() => {
                    if (hasChildren && sidebarOpen) {
                      setOpenMenus((prev) => ({ ...prev, [item.id]: !prev[item.id] }));
                    } else if (!hasChildren) {
                      handleMenuClick(item.path);
                    }
                  }}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative ${
                    active
                      ? colors.active
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                  title={!sidebarOpen ? item.label : ''}
                  aria-label={item.label}
                >
                  {/* Active Indicator */}
                  {active && !hasChildren && (
                    <div
                      className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`}
                    />
                  )}

                  <div
                    className={`flex-shrink-0 ${active ? '' : 'group-hover:scale-110'} transition-transform`}
                  >
                    <Icon className="w-5 h-5" />
                  </div>

                  {sidebarOpen && (
                    <>
                      <span className="flex-1 text-left font-medium text-sm truncate">
                        {item.label}
                      </span>

                      {item.comingSoon && (
                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-violet-100 text-violet-700 rounded-full">
                          Soon
                        </span>
                      )}

                      {hasChildren ? (
                        <div className="transition-transform duration-200">
                          {isOpen ? (
                            <ChevronDown className="w-4 h-4 text-slate-500" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                      ) : (
                        active && <ChevronRight className="w-4 h-4 flex-shrink-0" />
                      )}
                    </>
                  )}

                  {!sidebarOpen && hasActiveChild && (
                    <span
                      className={`absolute -top-1 -right-1 w-2 h-2 ${colors.indicator} rounded-full`}
                    />
                  )}
                </button>

                {/* Submenu — Expanded Sidebar */}
                {hasChildren && isOpen && sidebarOpen && (
                  <div className="mt-1 ml-3 pl-3 border-l-2 border-slate-200 space-y-0.5">
                    {item.children.map((child) => {
                      const ChildIcon = child.icon;
                      const childActive = location.pathname === child.path;
                      return (
                        <button
                          key={child.id}
                          onClick={() => handleMenuClick(child.path, item.id)}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                            childActive
                              ? colors.active
                              : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                          }`}
                        >
                          {childActive && (
                            <div
                              className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`}
                            />
                          )}
                          <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                            <ChildIcon className="w-4 h-4" />
                          </div>
                          <span className="flex-1 text-left truncate">{child.label}</span>
                          {childActive && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Submenu Popover — Collapsed Sidebar */}
                {showPopover &&
                  (() => {
                    const buttonElement = document.querySelector(
                      `button[aria-label="${item.label}"]`
                    );
                    const rect = buttonElement?.getBoundingClientRect();
                    return (
                      <div
                        className="fixed bg-white rounded-lg shadow-xl border border-slate-200 py-2 px-1 min-w-[200px] z-[100]"
                        style={{
                          left: `${(rect?.right || 0) + 8}px`,
                          top: `${rect?.top || 0}px`,
                        }}
                        onMouseEnter={handlePopoverEnter}
                        onMouseLeave={handlePopoverHide}
                      >
                        <div className="px-3 py-2 border-b border-slate-100 mb-1">
                          <p className="text-xs font-semibold text-slate-700 uppercase tracking-wide">
                            {item.label}
                          </p>
                        </div>
                        <div className="space-y-0.5">
                          {item.children.map((child) => {
                            const ChildIcon = child.icon;
                            const childActive = location.pathname === child.path;
                            return (
                              <button
                                key={child.id}
                                onClick={() => handleMenuClick(child.path, item.id)}
                                className={`w-full flex items-center gap-3 px-3 py-2.5 mx-1 rounded-lg text-sm font-medium transition-all duration-200 group relative ${
                                  childActive
                                    ? colors.active
                                    : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                              >
                                {childActive && (
                                  <div
                                    className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 ${colors.indicator} rounded-r-full`}
                                  />
                                )}
                                <div className="flex-shrink-0 group-hover:scale-110 transition-transform">
                                  <ChildIcon className="w-4 h-4" />
                                </div>
                                <span className="flex-1 text-left truncate">{child.label}</span>
                                {childActive && (
                                  <ChevronRight className="w-4 h-4 flex-shrink-0" />
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
              </div>
            );
          })
        )}
      </nav>

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <div className="p-3 border-t border-slate-200">
        {sidebarOpen ? (
          <div className="flex flex-col items-center gap-1">
            <span className="text-[10px] text-slate-400">A Product by</span>
            <a
              href={brandingConfig.companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="hover:opacity-80 transition-opacity"
            >
              <img src={brandingConfig.companyLogoUrl} alt={brandingConfig.companyName} className="h-12 max-w-[140px] object-contain" />
            </a>
            <span className="text-[10px] text-slate-400 mt-1">{`${brandingConfig.productName} ${brandingConfig.productVersion}`}</span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <a
              href={brandingConfig.companyWebsite}
              target="_blank"
              rel="noopener noreferrer"
              className="flex justify-center hover:opacity-80 transition-opacity"
              title={`A Product by ${brandingConfig.companyName}`}
            >
              <img src={brandingConfig.companyLogoUrl} alt={brandingConfig.companyName} className="h-8 max-w-[40px] object-contain" />
            </a>
            <span className="text-[9px] text-slate-400" title={`${brandingConfig.productName} ${brandingConfig.productVersion}`}>
              {brandingConfig.productVersion}
            </span>
          </div>
        )}
      </div>
    </aside>
  );
}